/**
 * API Service Module
 * Centralized API endpoint handling for the chat application
 */
import * as webauthn from "@github/webauthn-json";

 
import {
  base64urlToUint8Array,
  uint8ArrayToBase64url
} from "./webauthnutils";

/**
 * Environment-based API base URL
 *
 * Priority:
 * 1) REACT_APP_API_BASE_URL (explicit per environment)
 * 2) production: same-origin (works great when nginx proxies API routes)
 * 3) development: localhost
 */
const ENV_BASE_URL = process.env.REACT_APP_API_BASE_URL;
let BASE_URL =
  ENV_BASE_URL ||
  (process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:8012");

const PAYMENT_API_URL = BASE_URL + '/v2/travelagent/payment/init';

const SESSION_GRAPH_VISIBLE_URL = BASE_URL + '/v2/hotel/session';
const CHAT_API_URL = BASE_URL + '/v2/travelagent/chat';
const UI_STREAM_API_URL = BASE_URL + '/v2/travelagent/ui_stream';
const RESET_API_URL = BASE_URL + '/v2/travelagent/reset';
const LOGIN_API_URL = BASE_URL + '/v2/travelagent/login';
const VERIFY_PASSWORD_API_URL = BASE_URL + '/v2/travelagent/verify';
const LOGIN_API_URL_BEGIN = BASE_URL + '/v2/travelagent/login/begin';
const LOGIN_API_URL_FINISH = BASE_URL + '/v2/travelagent/login/finish';
const REGISTER_API_URL_BEGIN = BASE_URL + '/v2/travelagent/register/begin';
const REGISTER_API_URL_FINISH = BASE_URL + '/v2/travelagent/register/finish';
const SESSION_API_URL = BASE_URL + '/v2/hotel/session/create';
const AUTHENTICATE_API_URL_BEGIN = BASE_URL + '/v2/travelagent/authenticate/begin';
const AUTHENTICATE_API_URL_FINISH = BASE_URL + '/v2/travelagent/authenticate/finish';
const CHECK_USER_EXIST = BASE_URL + '/v2/travelagent/check/user';
const ADD_PAYMENT_CARD = BASE_URL + '/v2/card/payment/add';
const LIST_PAYMENT_CARD = BASE_URL + '/v2/card/payment/all';
const DELETE_PAYMENT_CARD = BASE_URL + '/v2/card/payment/delete';
const SET_DEFAULT_CARD = BASE_URL + '/v2/card/payment/set-default';
const GET_DEFAULT_CARD = BASE_URL + '/v2/card/payment/default';
const VALIDATE_OTP_API_URL = BASE_URL + '/v2/card/payment/validate/otp';
const CONTINUE_ADD_CARD_API_URL = BASE_URL + '/v2/card/payment/continue/addcard';

/**
 * Send chat message via non-streaming endpoint
 * @param {string} query - User message
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response data with message and widgets
 */
export const sendChatMessage = async (query, sessionId, userId) => {
  const requestBody = {
    query: query,
    session_id: sessionId || null,
    user_id: userId || null
  };

  const response = await fetch(CHAT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(errorData.detail || 'Request failed');
  }

  return await response.json();
};

/**
 * Send chat message via streaming endpoint (Server-Sent Events)
 * @param {string} query - User message
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {Function} onChunk - Callback function called for each chunk: (chunk) => void
 * @param {Function} onError - Callback function called on error: (error) => void
 * @param {Function} onComplete - Callback function called when stream completes: () => void
 * @returns {Promise<void>}
 */
export const sendChatMessageStream = async (query, sessionId, userId, onChunk, onError, onComplete) => {
  const requestBody = {
    query: query,
    session_id: sessionId || null,
    user_id: userId || null
  };

  try {
    const response = await fetch(UI_STREAM_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Request failed');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = ''; // Buffer for accumulating chunks that may contain partial JSON objects
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        // Process any remaining data in buffer
        if (buffer && buffer.trim()) {
          const lines = buffer.split('\n').filter(line => line.trim());
          for (const line of lines) {
            try {
              const obj = JSON.parse(line);
              if (obj.type === 'data' && onChunk) {
                // Log received component data for debugging
                // if (obj.payload?.component) {
                //   console.log(`📥 Received component:`, {
                //     id: obj.payload.component.id,
                //     type: obj.payload.component.type,
                //     parent: obj.payload.parent
                //   });
                // }
                onChunk(obj.payload);
              } else if (obj.type === 'status') {
                console.log('ℹ️ Status message:', obj.message);
              } else if (obj.type === 'close') {
                if (onComplete) onComplete();
                return;
              } else if (obj.type === 'error') {
                console.error('❌ Stream error:', obj.error);
                
                // Parse error message for specific error types
                let userMessage = 'An error occurred. Please try again.';
                const errorStr = typeof obj.error === 'string' ? obj.error : JSON.stringify(obj.error);
                
                // Check for rate limit errors (429)
                if (errorStr.includes('RateLimitReached') || errorStr.includes('429')) {
                  // Extract retry time if available
                  const retryMatch = errorStr.match(/retry after (\d+) seconds?/i);
                  const retrySeconds = retryMatch ? parseInt(retryMatch[1]) : 20;
                  userMessage = `⚠️ Rate limit reached. The AI service is currently at capacity. Please wait ${retrySeconds} seconds and try again.`;
                } else if (errorStr.includes('Error code:')) {
                  // Extract error code and message
                  const codeMatch = errorStr.match(/Error code: (\d+)/);
                  if (codeMatch) {
                    userMessage = `⚠️ Service error (${codeMatch[1]}). Please try again in a moment.`;
                  }
                }
                
                // Send error message to user through chunk callback
                if (onChunk) {
                  onChunk({
                    type: 'text',
                    text: userMessage,
                    error: true
                  });
                }
              }
            } catch (parseErr) {
              console.warn('⚠️ Failed to parse final buffer line:', parseErr.message, 'Line:', line.substring(0, 100));
            }
          }
        }
        if (onComplete) onComplete();
        break;
      }

      // Decode chunk and add to buffer
      const chunkStr = decoder.decode(value, { stream: true });
      buffer += chunkStr;

      // Backend sends JSON objects separated by \n
      // Split by \n and process each complete JSON object
      const lines = buffer.split('\n');
      // Keep the last potentially incomplete line in buffer
      buffer = lines.pop() || '';

      // Process each complete line (complete JSON object)
      for (const line of lines) {
        if (!line.trim()) {
          continue; // Skip empty lines
        }

        try {
          // Each line is a complete JSON object
          // Format: {"type":"data","payload":{...}} or {"type":"close"} or {"type":"error","error":"..."}
          const obj = JSON.parse(line);
          
          if (obj.type === 'data') {
            if (onChunk) {
              // Log received component data for debugging
              // if (obj.payload?.component) {
              //   console.log(`📥 Received component:`, {
              //     id: obj.payload.component.id,
              //     type: obj.payload.component.type,
              //     parent: obj.payload.parent
              //   });
              // }
              onChunk(obj.payload);
            }
            consecutiveErrors = 0;
          } else if (obj.type === 'status') {
            console.log('ℹ️ Status message:', obj.message);
          } else if (obj.type === 'close') {
            if (onComplete) onComplete();
            return;
          } else if (obj.type === 'error') {
            console.error('❌ Stream error:', obj.error);
            
            // Parse error message for specific error types
            let userMessage = 'An error occurred. Please try again.';
            const errorStr = typeof obj.error === 'string' ? obj.error : JSON.stringify(obj.error);
            
            // Check for rate limit errors (429)
            if (errorStr.includes('RateLimitReached') || errorStr.includes('429')) {
              // Extract retry time if available
              const retryMatch = errorStr.match(/retry after (\d+) seconds?/i);
              const retrySeconds = retryMatch ? parseInt(retryMatch[1]) : 20;
              userMessage = `⚠️ Rate limit reached. The AI service is currently at capacity. Please wait ${retrySeconds} seconds and try again.`;
            } else if (errorStr.includes('Error code:')) {
              // Extract error code and message
              const codeMatch = errorStr.match(/Error code: (\d+)/);
              if (codeMatch) {
                userMessage = `⚠️ Service error (${codeMatch[1]}). Please try again in a moment.`;
              }
            }
            
            // Send error message to user through chunk callback
            if (onChunk) {
              onChunk({
                type: 'text',
                text: userMessage,
                error: true
              });
            }
            
            consecutiveErrors++;
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              console.error('❌ Too many consecutive errors, stopping stream');
              if (onError) {
                onError(new Error('Stream parse error'));
              }
              return;
            }
          }
        } catch (parseErr) {
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('❌ Too many consecutive parse errors, stopping stream');
            if (onError) {
              onError(new Error('Stream parse error'));
            }
            return;
          }
          console.warn('⚠️ Failed to parse JSON line:', parseErr.message, 'Line:', line.substring(0, 100));
        }
      }
    }
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};


/**
 * Reset conversation session
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export const resetSession = async (sessionId) => {
  if (!sessionId) return;

  try {
    await fetch(RESET_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    });
  } catch (error) {
    console.error('Reset failed:', error);
    throw error;
  }
};

/**
 * Register user
 */
/**
 * Register user
 * @param {Object} userData - User registration data (e.g., { username, password, email })
 * @returns {Promise<Object>} Response data
 */

export const checkIfUserExist = async (username) => {
  if (!username) {
    throw new Error('User data is required');
  }

  //check if user exist
  const response = await fetch(`${CHECK_USER_EXIST}?username=${username}`)
  return await response.json();

}

export const registerUser = async (username, session_id, isMastercardPaymentPasskey) => {
  if (!username) {
    throw new Error('User data is required');
  }

  const resp = await fetch(`${REGISTER_API_URL_BEGIN}?username=${username}`)
  const options = await resp.json();
  
  console.warn("Register options:", options);

  options.challenge = base64urlToUint8Array(options.challenge);
  options.user.id = base64urlToUint8Array(options.user.id);

  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(cred => ({
      ...cred,
      id: base64urlToUint8Array(cred.id)
    }));
  }

  // 3️⃣ Create credential
  const credential = await navigator.credentials.create({
    publicKey: options
  });
  console.warn("calling register finish")
  
  // 4️⃣ Send credential to backend
  
  const response = await fetch(REGISTER_API_URL_FINISH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: credential.id,
      username:username,
      sessionid: session_id,
      isMastercardPaymentPasskey: isMastercardPaymentPasskey,
      rawId: uint8ArrayToBase64url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: uint8ArrayToBase64url(
          credential.response.clientDataJSON
        ),
        attestationObject: uint8ArrayToBase64url(
          credential.response.attestationObject
        )
      }
    })
  })
  const res =  await response.json();
  console.warn("Register finish response:", res);
  return res;
};

/**
 * Verify login password
 * @param {string} password - User password
 * @returns {Promise<Object>} Response data with status
 */
export const loginPasskey = async (username) => {
 // 1️⃣ Get authentication options
 console.warn("Login username:", username);
 
 const response = await fetch(LOGIN_API_URL_BEGIN, {
  method: "POST",
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ username })
});
const options = await response.json();
console.warn("Login options:", options);
// 2️⃣ Convert fields
options.challenge = base64urlToUint8Array(options.challenge);
options.allowCredentials = options.allowCredentials.map(cred => ({
 ...cred,
 id: base64urlToUint8Array(cred.id)
}));

console.warn("calling login get assertion")
// 3️⃣ Get assertion
const assertion = await navigator.credentials.get({
 publicKey: options
});

console.warn("calling login finish")

// 4️⃣ Send assertion to backend
const finishresp = await fetch(LOGIN_API_URL_FINISH, {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({
   id: assertion.id,
   username:username,
   rawId: uint8ArrayToBase64url(assertion.rawId),
   type: assertion.type,
   response: {
     clientDataJSON: uint8ArrayToBase64url(
       assertion.response.clientDataJSON
     ),
     authenticatorData: uint8ArrayToBase64url(
       assertion.response.authenticatorData
     ),
     signature: uint8ArrayToBase64url(
       assertion.response.signature
     ),

     userHandle: assertion.response.userHandle
       ? uint8ArrayToBase64url(assertion.response.userHandle)
       : null
   }
 })
});

  const res =  await finishresp.json();
  console.warn("Login finish response:", res);
  return res

};


/**
 * Authenticate Passkey 
 * @param {string} password - User password
 * @returns {Promise<Object>} Response data with status
 */
export const authenticatePasskey = async (username, session_id, intentId) => {
  // 1️⃣ Get authentication options

  
  const response = await fetch(AUTHENTICATE_API_URL_BEGIN, {
   method: "POST",
   headers: {
     "Content-Type": "application/json"
   },
   body: JSON.stringify({ username , session_id})
 });
 const options = await response.json();
 console.log(options)
 // 2️⃣ Convert fields
 options.challenge = base64urlToUint8Array(options.challenge);
 options.allowCredentials = options.allowCredentials.map(cred => ({
  ...cred,
  id: base64urlToUint8Array(cred.id)
 }));
 
 // 3️⃣ Get assertion
 const assertion = await navigator.credentials.get({
  publicKey: options
 });
 
 
 // 4️⃣ Send assertion to backend
 const finishresp = await fetch(AUTHENTICATE_API_URL_FINISH, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    id: assertion.id,
    username:username,
    session_id:session_id,
    rawId: uint8ArrayToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: uint8ArrayToBase64url(
        assertion.response.clientDataJSON
      ),
      authenticatorData: uint8ArrayToBase64url(
        assertion.response.authenticatorData
      ),
      signature: uint8ArrayToBase64url(
        assertion.response.signature
      ),
 
      userHandle: assertion.response.userHandle
        ? uint8ArrayToBase64url(assertion.response.userHandle)
        : null
    }
  })
 })
 .then(res => res.json());
 
 return finishresp

 };

/**
 * Verify login password
 * @param {string} password - User password
 * @returns {Promise<Object>} Response data with status
 */
export const verifyPassword = async (password) => {
  if (!password) {
    throw new Error('Password is required');
  }

  const response = await fetch(VERIFY_PASSWORD_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
    throw new Error(errorData.detail || 'Login failed');
  }

  return await response.json();
};

/**
 * Create a new session
 * @param {string} userId - User ID
 * @param {string} userType - User type (budget, luxury, family, adventure, cultural, solo, business)
 * @returns {Promise<Object>} Response data with session_id
 */
export const createSession = async (userId, userType) => {
  const response = await fetch(SESSION_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      user_id: userId || null,
      user_type: userType || null
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to create session' }));
    throw new Error(errorData.detail || 'Failed to create session');
  }

  return await response.json();
};

/**
 * Confirm payment and initiate payment process
 * @param {string} password - Payment password
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {Object} paymentData - Payment data
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Response data with status
 */
export const confirmPayment = async (password, sessionId, userId, paymentData, orderId, isCashBack = false) => {
 
  var newPassword = "123456"
  var requestBody = {
    session_id: sessionId || null,
    user_id: userId || null,
    isVerified: true,
    payment_data: paymentData || {},
    order_id: orderId || null,
    cash_back_percentage: isCashBack ? 5 : 0
  };

 
  requestBody.password = newPassword;
  
  const response = await fetch(PAYMENT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  });

  const data = await response.json();

  if (data.status === 'success') {
    return data;
  } else {
    throw new Error(data.detail || data.message || 'Unknown error');
  }
};



/**
 * Add Payment Card
 * @param {string} user_id - User id 
 * @param {string} card_number -Card number 16 digits
 * @param {string} expiry -Card number 16 digits
 * @param {string} name_on_card -Card number 16 digits
 * @returns {Promise<Object>} Response data with status
 */
export const addPaymentCard = async (user_id, sessionId, cardNumber, expiryDate, cvv, cardHolder) => {
  
  console.log("addPaymentCard sessionId:", sessionId, "userId:", user_id);
 
  const response = await fetch(ADD_PAYMENT_CARD, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      user_id: user_id || null,
      session_id: sessionId || null,
      cardNumber: cardNumber || null,
      expiryDate : expiryDate || null,
      cvv:cvv || null,
      cardHolder : cardHolder || null
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Enroll card  failed' }));
    throw new Error(errorData.detail || 'Enroll card failed');
  }

  const data = await response.json();
  console.log("addPaymentCard response data:", data);

  // Check if OTP authentication is required
  if (data.authenticationMethod === 'OTP') {
    try {
    
      return { status: 'SMS_OTP', card_id: data.card_id };
    } catch (error) {
      throw new Error('OTP required');
    }
  }
  else  {
    try {
      return { status: 'PASSKEY', card_id: data.card_id };
    } catch (error) {
      throw new Error('Passkey authentication required');
    }
  }
  

};

/**
 * Set Default Payment Card
 * @param {string} user_id - User id
 * @param {string} card_id - Card id
 * @returns {Promise<Object>} Response data
 */
export const setDefaultCard = async (user_id, card_id) => {
  const response = await fetch(SET_DEFAULT_CARD, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id, card_id }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to set default card' }));
    throw new Error(errorData.detail || 'Failed to set default card');
  }

  return await response.json();
};

/**
 * Get Default Payment Card
 * @param {string} user_id - User id
 * @returns {Promise<Object>} Response data with default_card_id
 */
export const getDefaultCard = async (user_id) => {
  const response = await fetch(`${GET_DEFAULT_CARD}/${user_id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to get default card' }));
    throw new Error(errorData.detail || 'Failed to get default card');
  }

  return await response.json();
};

/**
 * Get All Payment Cards
 * @param {string} user_id - User id
 * @returns {Promise<Array>} List of payment cards
 */
export const getAllPaymentCards = async (user_id) => {
  const response = await fetch(`${LIST_PAYMENT_CARD}/${user_id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to get cards' }));
    throw new Error(errorData.detail || 'Failed to get cards');
  }

  return await response.json();
};

/**
 * Delete Payment Card
 * @param {string} user_id - User id
 * @param {string} card_id - Card id
 * @returns {Promise<Object>} Response data
 */
export const deletePaymentCard = async (user_id, card_id) => {
  const response = await fetch(DELETE_PAYMENT_CARD, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ user_id, card_id }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to delete card' }));
    throw new Error(errorData.detail || 'Failed to delete card');
  }

  return await response.json();
};



/**
 * Update graph visibility state for a session
 * When graph is visible, artificial delays are enabled for visualization.
 * When graph is hidden, delays are skipped for faster processing.
 * @param {string} sessionId - Session ID
 * @param {boolean} isVisible - Whether the graph panel is visible
 * @returns {Promise<Object>} Response data with status
 */
export const updateGraphVisibility = async (sessionId, isVisible) => {
  if (!sessionId) {
    console.warn('Cannot update graph visibility: no session ID');
    return;
  }

  try {
    const response = await fetch(`${SESSION_GRAPH_VISIBLE_URL}/${sessionId}/graph-visible`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ graph_visible: isVisible })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to update graph visibility' }));
      console.error('Failed to update graph visibility:', errorData.detail);
      return;
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating graph visibility:', error);
  }
};

/**
 * Validate OTP for user authentication
 * @param {string} username - Username
 * @param {string} sessionId - Session ID
 * @param {string} otp - One-time password to validate
 * @returns {Promise<Object>} Response data with status
 */
export const validateOtp = async (username, sessionId, otp) => {
  if (!username || !sessionId || !otp) {
    throw new Error('Username, session ID, and OTP are required');
  }

  const response = await fetch(VALIDATE_OTP_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      session_id: sessionId,
      otp
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'OTP validation failed' }));
    throw new Error(errorData.detail || 'OTP validation failed');
  }

  const data = await response.json();
  console.log("validateOtp response:", data);
  // Check if passkey authentication is required
  if (data.status === 'require_passkey') {
    try {
      // Trigger passkey authentication
     
      return { status: 'ok', passkey_authenticated: true };
    } catch (error) {
      throw new Error('Passkey authentication required but failed');
    }
  }

  return data;
};

/**
 * Continue adding card after passkey authentication
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @param {string} cardId - Card ID
 * @returns {Promise<Object>} Response data with success status
 */
export const continueAddCard = async (userId, sessionId, cardId) => {
  if (!userId || !sessionId || !cardId) {
    throw new Error('User ID, session ID, and card ID are required');
  }

  const response = await fetch(CONTINUE_ADD_CARD_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_id: userId,
      session_id: sessionId,
      card_id: cardId
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to continue adding card' }));
    throw new Error(errorData.detail || 'Failed to continue adding card');
  }

  return await response.json();
};